import type { TableFeatures, Table as TanStackTable } from '@tanstack/react-table';
import clsx from 'clsx';
import { ChevronsLeft, ChevronsRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { HubertButton } from 'components/HubertUI/HubertButton';
import type { ComponentProps } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TPaginationResult } from 'rtk/query/apis/api-common.types';
import type { FuzzyFilterMeta } from '../helpers/Helpers';

function createEquallySpacedArray(start: number, end: number, numPoints: number): number[] {
  // Calculate the step size and round up to
  // ensure integers and end value is in
  const step = Math.ceil((end - start) / (numPoints - 1));
  let result: number[] = [];
  if (end < 50) {
    result = [50];
  } else {
    for (let i = 0; i < numPoints; i++) {
      const value = start + i * step;
      result.push(value);
    }
  }
  return result;
}

interface Props extends ComponentProps<'div'> {
  searchResults?: TPaginationResult<any>;
  table: TanStackTable<TableFeatures & { filterMeta: FuzzyFilterMeta }, any>;
  selectedRows: any;
  rowSelectionEnabled: boolean;

  first: {
    onClick: () => void;
  };
  previous: {
    onClick: () => void;
  };
  next: {
    onClick: () => void;
  };
  last: {
    onClick: () => void;
  };
}

const PaginationBar = ({
  searchResults,
  table,
  selectedRows,
  rowSelectionEnabled = false,
  first,
  previous,
  next,
  last,
}: Props) => {
  const { t } = useTranslation('PaginatedManagerTable');
  const [startIndex, setStartIndex] = useState<number>();
  const [endIndex, setEndIndex] = useState<number>();

  const [, setPageSizeOptions] = useState<number[]>([10, 20, 30, 40, 50]);

  useEffect(() => {
    if (searchResults) {
      setPageSizeOptions(createEquallySpacedArray(0, searchResults?.totalItems, 5));
    }
  }, [searchResults]);

  useEffect(() => {
    if (searchResults) {
      setStartIndex(searchResults.pageIndex * searchResults.pageSize);

      let tempEndIndex = (searchResults.pageIndex + 1) * searchResults.pageSize;
      if (tempEndIndex > searchResults.totalItems) {
        tempEndIndex = searchResults.totalItems;
      }
      setEndIndex(tempEndIndex);
    }
  }, [searchResults]);

  if (!searchResults) {
    return <div className="bg-danger-50 text-danger-400 rounded-md p-2">{t('pagination.error')}</div>;
  }

  return (
    <div
      className={clsx(
        'flex flex-row items-center justify-between',
        'p-3 px-4',
        'rounded-lg border border-gray-200 bg-white',
        'shadow-sm',
      )}
    >
      <div className={clsx('flex flex-row items-center gap-4', 'text-sm text-gray-600')}>
        {searchResults && startIndex !== undefined && endIndex !== undefined ? (
          <div className="font-medium">
            {t('pagination.showing', { startIndex, endIndex, totalItems: searchResults.totalItems })}
          </div>
        ) : null}

        {rowSelectionEnabled ? (
          <div className="flex items-center gap-2">
            <div className="font-medium">
              {t('pagination.selected', {
                count: Object.keys(selectedRows).length,
                total: table.getPreFilteredRowModel().rows.length,
              })}
            </div>
          </div>
        ) : null}
      </div>

      <div className={clsx('flex flex-row items-center gap-4', 'rounded-md p-1')}>
        <div className={clsx('flex flex-row items-center gap-1')}>
          <HubertButton
            variant={'outline-borderless'}
            size={'sm'}
            disabled={searchResults.pageIndex === 0}
            onClick={first.onClick}
            iconLeft={<ChevronsLeft className="h-4 w-4" />}
            className={clsx(
              'px-2 py-1.5',
              'text-info-600 hover:text-info-700',
              'hover:bg-info-50 active:bg-info-200',
              'disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-transparent',
              'transition-colors duration-150',
              'rounded-md',
              'font-medium',
            )}
          >
            {t('pagination.first')}
          </HubertButton>

          <HubertButton
            variant={'outline-borderless'}
            size={'sm'}
            disabled={!searchResults.hasPreviousPage}
            onClick={previous.onClick}
            iconLeft={<ChevronLeft className="h-4 w-4" />}
            className={clsx(
              'px-2 py-1.5',
              'text-info-600 hover:text-info-700',
              'hover:bg-info-50 active:bg-info-200',
              'disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-transparent',
              'transition-colors duration-150',
              'rounded-md',
              'font-medium',
            )}
          >
            {t('pagination.previous')}
          </HubertButton>
        </div>

        <div className={clsx('flex flex-row items-center gap-1')}>
          <HubertButton
            variant={'outline-borderless'}
            size={'sm'}
            disabled={!searchResults.hasNextPage}
            onClick={next.onClick}
            icon={<ChevronRight className="h-4 w-4" />}
            className={clsx(
              'px-2 py-1.5',
              'text-info-600 hover:text-info-700',
              'hover:bg-info-50 active:bg-info-200',
              'disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-transparent',
              'transition-colors duration-150',
              'rounded-md',
              'font-medium',
            )}
          >
            {t('pagination.next')}
          </HubertButton>

          <HubertButton
            variant={'outline-borderless'}
            size={'sm'}
            disabled={!(searchResults.pageIndex < searchResults.totalPages - 1)}
            onClick={last.onClick}
            icon={<ChevronsRight className="h-4 w-4" />}
            className={clsx(
              'px-2 py-1.5',
              'text-info-600 hover:text-info-700',
              'hover:bg-info-50 active:bg-info-200',
              'disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:bg-transparent',
              'transition-colors duration-150',
              'rounded-md',
              'font-medium',
            )}
          >
            {t('pagination.last')}
          </HubertButton>
        </div>
      </div>

      <div className={clsx('flex flex-row items-center gap-4', 'text-sm text-gray-600')}>
        {searchResults && searchResults.totalPages > 0 ? (
          <div className="font-medium">
            {t('pagination.pageOf', { currentPage: searchResults.pageIndex + 1, pageCount: searchResults.totalPages })}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PaginationBar;
